import { EventEmitter } from "@stencil/core";
import { Component, Event, Element, Host, Prop, State, h } from "@stencil/core";
import { track } from "../../utils/tracking";

/**
 * This component fetches a href from an endpoint and puts it in the element provided in the "link" slot.
 * If the fetch fails, the element provided in the "fallback" slot is shown.
 * @slot link - The element that should contain the href (must be wrapped in another element like div or span)
 * @slot fallback - The element that should be shown if the fetch fails
 */
@Component({
    tag: "ux-iws-dynamic-href",
    shadow: true,
})
export class DynamicHref {
    @Element() el: HTMLUxIwsDynamicHrefElement;
    /** The endpoint to fetch the href from */
    @Prop() endpoint: string = "";

    @State() href: string = "";

    /**
     * Event that is emitted when the href is loaded.
     */
    @Event({
        eventName: "uxIwsDynamicHrefLoaded",
        composed: true,
        bubbles: true,
    })
    dynamicHrefLoaded: EventEmitter<{ href: string }>;

    componentDidLoad() {
        if (this.endpoint === "") {
            return;
        }
        fetch(this.endpoint)
            .then((response) => {
                if (!response.ok) {
                    throw new Error("Failed to fetch");
                }
                return response.json();
            })
            .then(({ href }: { href: string | null | undefined }) => {
                if (this.href === href || href === null || href === undefined)
                    return;
                this.href = href;
                this.dynamicHrefLoaded.emit({ href });
                const linkElement = this.el.querySelector(
                    "[slot='link'] [href]"
                );
                linkElement?.setAttribute("href", this.href);
                linkElement?.setAttribute("data-testid", "dynamic-href-link");
            });
        const trackingAttribute = "data-track-event-name";
        this.el
            .querySelector(`[slot='link'] [${trackingAttribute}]`)
            ?.addEventListener("click", (e: Event) => {
                const eventName = (e.target as Element)?.getAttribute(
                    trackingAttribute
                );
                track(eventName, {}, true);
            });
    }

    render() {
        return (
            <Host>
                {this.href !== "" && <slot name="link"></slot>}
                {this.href === "" && <slot name="fallback"></slot>}
            </Host>
        );
    }
}
